
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

export type Category = {
  id: string;
  description: string | null;
  created_at: string;
  category_group_id?: string | null;
  translation_key: string;
  name: string; // Translated name
};

export const useCategories = () => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;

  return useQuery({
    queryKey: ['categories', currentLocale],
    queryFn: async () => {
      // Use a left join instead of inner join to avoid missing categories
      const { data, error } = await supabase
        .from('categories')
        .select(`
          id, 
          description, 
          created_at, 
          category_group_id, 
          translation_key,
          translation_defaults(key, default_value)
        `)
        .order('translation_key');

      if (error) {
        console.error('Error fetching categories:', error);
        throw new Error(error.message);
      }

      // Fetch community-specific translations if they exist
      const { data: communityTranslations, error: communityTranslationsError } = await supabase
        .from('community_translations')
        .select('translation_key, value')
        .eq('locale', currentLocale)
        .in('translation_key', data.map(c => c.translation_key));

      if (communityTranslationsError) {
        console.error('Error fetching community translations:', communityTranslationsError);
      }

      // Create a map of translation overrides
      const translationOverrides = new Map();
      if (communityTranslations) {
        communityTranslations.forEach(t => {
          translationOverrides.set(t.translation_key, t.value);
        });
      }

      // Process the categories with their translations
      const processedCategories = data.map(category => {
        // Handle translation_defaults safely - it could be an array or object
        let defaultName = '';
        
        // Check if translation_defaults exists
        if (category.translation_defaults) {
          // It could be an array with one object
          if (Array.isArray(category.translation_defaults) && category.translation_defaults.length > 0) {
            defaultName = category.translation_defaults[0].default_value || '';
          } 
          // Or it could be a direct object
          else if (typeof category.translation_defaults === 'object') {
            defaultName = category.translation_defaults.default_value || '';
          }
        }
        
        // If defaultName is still empty, use translation_key as fallback
        if (!defaultName) {
          defaultName = category.translation_key || 'Unnamed category';
        }
        
        // Check if there's a community-specific translation override
        const translatedName = translationOverrides.get(category.translation_key) || defaultName;
        
        return {
          id: category.id,
          description: category.description,
          created_at: category.created_at,
          category_group_id: category.category_group_id,
          translation_key: category.translation_key,
          name: translatedName || 'Unnamed category', // Ensure there's always a name
        };
      });

      return {
        categories: processedCategories,
        categoriesList: processedCategories
      };
    },
  });
};
